import { useState, useEffect } from "react";
import axios from "axios";
import {
  Settings2,
  Loader2,
  Save,
  RotateCcw,
  Info,
  AlertCircle,
} from "lucide-react";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Slider } from "./ui/slider";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Alert,
  AlertDescription,
} from "./ui/alert";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_WEIGHTS = {
  required_skills: 40,
  experience: 30,
  education: 15,
  optional_skills: 15,
};

const WEIGHT_CONFIG = [
  {
    key: "required_skills",
    label: "Habilidades Requeridas",
    description: "Coincidencia con skills obligatorias del puesto",
    color: "bg-indigo-500",
  },
  {
    key: "experience",
    label: "Experiencia Relevante",
    description: "Años y tipo de experiencia laboral",
    color: "bg-blue-500",
  },
  {
    key: "education",
    label: "Educación",
    description: "Nivel educativo y formación académica",
    color: "bg-purple-500",
  },
  {
    key: "optional_skills",
    label: "Habilidades Deseables",
    description: "Skills opcionales que suman valor",
    color: "bg-teal-500",
  },
];

export default function ScoringWeightsEditor({ jobId, currentWeights, open, onClose, onSave }) {
  const [weights, setWeights] = useState(currentWeights || DEFAULT_WEIGHTS);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (open) {
      setWeights(currentWeights || DEFAULT_WEIGHTS);
      setHasChanges(false);
    }
  }, [open, currentWeights]);

  const total = weights.required_skills + weights.experience + weights.education + weights.optional_skills;
  const isValid = total === 100;

  const handleWeightChange = (key, value) => {
    const newValue = value[0];
    setWeights(prev => ({
      ...prev,
      [key]: newValue
    }));
    setHasChanges(true);
  };

  const handleReset = () => {
    setWeights(DEFAULT_WEIGHTS);
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!isValid) {
      toast.error("Los pesos deben sumar 100%");
      return;
    }

    setSaving(true);
    try {
      const response = await axios.put(`${API}/jobs/${jobId}/scoring-weights`, weights);
      toast.success(`Pesos actualizados. ${response.data.candidates_recalculated} candidatos recalculados.`);
      onSave?.(weights);
      onClose();
    } catch (error) {
      console.error("Error updating weights:", error);
      const detail = error.response?.data?.detail || "Error al actualizar los pesos";
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Settings2 className="w-5 h-5 text-primary" />
            Configurar Pesos del Scoring
          </DialogTitle>
          <DialogDescription>
            Ajustá la importancia de cada criterio según las necesidades de este puesto.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Info Alert */}
          <Alert className="bg-blue-50 border-blue-200">
            <Info className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-700 text-sm">
              Los pesos determinan cuánto influye cada criterio en el score final. 
              Los candidatos existentes serán recalculados automáticamente.
            </AlertDescription>
          </Alert>

          {/* Weight Sliders */}
          <div className="space-y-5">
            {WEIGHT_CONFIG.map((config) => (
              <div key={config.key} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`w-3 h-3 rounded-full ${config.color}`} />
                    <Label className="font-medium">{config.label}</Label>
                  </div>
                  <span className="text-lg font-bold text-primary">
                    {weights[config.key]}%
                  </span>
                </div>
                <Slider
                  value={[weights[config.key]]}
                  onValueChange={(value) => handleWeightChange(config.key, value)}
                  max={100}
                  min={0}
                  step={5}
                  className="cursor-pointer"
                />
                <p className="text-xs text-muted-foreground">
                  {config.description}
                </p>
              </div>
            ))}
          </div>

          {/* Total */}
          <div className={`p-4 rounded-lg border-2 ${isValid ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
            <div className="flex items-center justify-between">
              <span className={`font-medium ${isValid ? 'text-emerald-700' : 'text-red-700'}`}>
                Total
              </span>
              <span className={`text-2xl font-bold ${isValid ? 'text-emerald-600' : 'text-red-600'}`}>
                {total}%
              </span>
            </div>
            {!isValid && (
              <p className="text-sm text-red-600 mt-2 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                Los pesos deben sumar exactamente 100%
              </p>
            )}
          </div>

          {/* Preview */}
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">Vista previa de distribución</Label>
            <div className="h-4 rounded-full overflow-hidden flex">
              {WEIGHT_CONFIG.map((config) => (
                <div
                  key={config.key}
                  className={`${config.color} transition-all duration-300`}
                  style={{ width: `${weights[config.key]}%` }}
                  title={`${config.label}: ${weights[config.key]}%`}
                />
              ))}
            </div>
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Skills Req.</span>
              <span>Experiencia</span>
              <span>Educación</span>
              <span>Skills Opt.</span>
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="ghost"
            onClick={handleReset}
            disabled={saving}
            className="gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            Valores por defecto
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving || !isValid || !hasChanges}
              className="gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Guardar y Recalcular
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
